import type { AfterViewInit, OnDestroy, TemplateRef } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    ViewChild,
    ViewContainerRef,
    computed,
    inject,
    input,
} from '@angular/core';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { Store } from '@ngrx/store';

import type { FormInputSettingsData } from '@sites/entity-core';
import type { PaymentFormWidgetForm } from '@sites/entity-signal-form';

import type { WebLegacyWidgetEntity } from '@web/types';
import type { PaymentFormWidgetData } from '@web/widgets/payment-form/type/payment-form-widget.type';

import { EPaymentView } from '@common/enums/payment-form-view.enum';
import { previewPortalAttachAction } from '@web/store/page-structure/widgets/widgets.actions';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { SpFormInputSettingsSignalComponent } from '@components/sp-form-input-settings-signal';
import { PaymentFormModalComponent } from './payment-form-modal/payment-form-modal-preview.component';
import { FormSettingsPanel } from '@web/widgets/common/abstract/form-settings-panel';

/**
 * Breadcrumbs/footer chrome around `sp-form-input-settings-signal`, writing
 * straight into the payment-form signal form. Save/cancel and the transaction
 * frame come from `FormSettingsPanel`. In the modal form view the modal preview
 * is rendered through a portal and reads a live widget snapshot from the form
 * so it tracks edits.
 */
@Component({
    selector: 'web-payment-input-settings',
    templateUrl: './payment-input-settings.component.html',
    styleUrls: ['./payment-input-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpFormInputSettingsSignalComponent, PaymentFormModalComponent],
})
export class PaymentInputSettingsComponent
    extends FormSettingsPanel<PaymentFormWidgetForm>
    implements AfterViewInit, OnDestroy
{
    public readonly formView = input<EPaymentView>();

    @ViewChild('modalPreviewPortal') public modalPreviewPortal: TemplateRef<unknown>;

    private readonly store$: Store = inject(Store);
    private readonly document: Document = inject(DOCUMENT);
    private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
    private portalHost: DomPortalOutlet;

    public readonly settings = computed<FormInputSettingsData>(() => this.form().inputSettings().value());

    /** Live widget snapshot for the display-only modal preview. */
    public readonly widgetSnapshot = computed(
        () => this.form()._raw().value() as unknown as WebLegacyWidgetEntity<PaymentFormWidgetData>
    );

    public onSettingsChange(value: FormInputSettingsData): void {
        this.form().setInputSettings(value);
    }

    public ngAfterViewInit(): void {
        if (this.isModalView) {
            const portalHTMLElement = this.document.querySelector('#modalPreviewContent');
            this.portalHost = new DomPortalOutlet(portalHTMLElement);

            this.portalHost.attach(new TemplatePortal(this.modalPreviewPortal, this.viewContainerRef));
            this.store$.dispatch(previewPortalAttachAction.modalPreviewToggle({ attached: true }));
        }
    }

    public ngOnDestroy(): void {
        if (this.isModalView) {
            this.portalHost.detach();
            this.store$.dispatch(previewPortalAttachAction.modalPreviewToggle({ attached: false }));
        }
    }

    private get isModalView(): boolean {
        return this.formView() === EPaymentView.modal;
    }
}
